import { INGREDIENT_CARDS } from '../../games/ideatthat/data/ingredientCards'

/**
 * Offline persona templates for AI players.
 *
 * Each persona has a `style_key` that matches `ai_players.style_key` in
 * the DB. Given an AI's hand, `generateCoursesFor(styleKey, hand)` picks
 * ingredients and slots them into that persona's templates — no LLM call
 * required, so Quick Play can be tested without API keys.
 *
 * When real LLM keys are wired up later, the edge function replaces this
 * path; the `submit_courses_as_ai` RPC signature stays identical so the
 * swap is one function call away.
 */

export type StyleKey = 'chef' | 'goblin' | 'haiku' | 'critic' | 'tourist' | 'kid'

export interface CourseOutput { text: string; ids: string[] }
export interface ThreeCourseOutput { c1: CourseOutput; c2: CourseOutput; c3: CourseOutput }

// ─── Ingredient lookup ────────────────────────────────────────────────────
const NAME_BY_ID = new Map(INGREDIENT_CARDS.map(c => [c.id, c.name]))
function labelOf(id: string): string { return NAME_BY_ID.get(id) ?? id }

// ─── Seeded RNG so the same hand always produces the same story ──────────
// Uses a tiny xorshift keyed by a string hash. Deterministic output means
// you can replay a room for debugging and get identical AI content.
function hash32(s: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h || 1
}
function makeRng(seed: string): () => number {
  let state = hash32(seed)
  return () => {
    state ^= state << 13; state >>>= 0
    state ^= state >>> 17
    state ^= state << 5;  state >>>= 0
    return state / 0xffffffff
  }
}
function pickN<T>(arr: T[], n: number, rng: () => number): T[] {
  const copy = arr.slice()
  const out: T[] = []
  while (copy.length && out.length < n) {
    const idx = Math.floor(rng() * copy.length)
    out.push(copy.splice(idx, 1)[0])
  }
  return out
}
function choice<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]
}

// ─── Template library ─────────────────────────────────────────────────────
// Each entry is a function that takes 3 ingredient names and returns a
// single sentence. Keeping them as functions (not format strings) lets
// each persona reshape or skip ingredients. 20 templates per course per
// persona gives plenty of variety so the same AI doesn't repeat itself
// across a handful of games.
type Template = (a: string, b: string, c: string) => string

const TEMPLATES: Record<StyleKey, { c1: Template[]; c2: Template[]; c3: Template[] }> = {
  chef: {
    c1: [
      (a, b, c) => `A delicate ${a.toLowerCase()} tartare, dressed with ${b.toLowerCase()} oil and a whisper of ${c.toLowerCase()}.`,
      (a, b) => `House-cured ${a.toLowerCase()} over a warm ${b.toLowerCase()} velouté. Simple, honest.`,
      (a, b, c) => `A tiny parcel of ${a.toLowerCase()}, ${b.toLowerCase()} and ${c.toLowerCase()}. My grandmother's recipe.`,
      (a, b) => `Three thin slices of ${a.toLowerCase()}, a drop of ${b.toLowerCase()}, fleur de sel.`,
      (a, b) => `${a} carpaccio beneath a ${b.toLowerCase()} gelée. You eat it in one breath.`,
      (a, b) => `An amuse of ${a.toLowerCase()} foam, ${b.toLowerCase()} pearls, a single leaf.`,
      (a, b, c) => `${a} crudo with ${b.toLowerCase()} vinaigrette and cracked ${c.toLowerCase()}.`,
      (a, b) => `A chilled ${a.toLowerCase()} consommé, cleared with ${b.toLowerCase()} and served in a spoon.`,
      (a, b) => `${a} confit, barely warm, against a raw ${b.toLowerCase()} shave.`,
      (a, b, c) => `One bite. ${a}, ${b.toLowerCase()}, and the memory of ${c.toLowerCase()}.`,
      (a, b) => `Torchon of ${a.toLowerCase()} with ${b.toLowerCase()} brûlée. Sweet, then savoury.`,
      (a, b) => `${a} marinated overnight in ${b.toLowerCase()}, brought to temperature in the palm.`,
      (a, b) => `A whisper of ${a.toLowerCase()}, lifted by ${b.toLowerCase()}. We trust the diner.`,
      (a, b) => `Raw ${a.toLowerCase()} dressed with warm ${b.toLowerCase()} butter. Nothing else has a right to be here.`,
      (a, b, c) => `${a} mille-feuille, ${b.toLowerCase()} cream, ${c.toLowerCase()} dust.`,
      (a, b) => `A thimble of ${a.toLowerCase()} broth, a single ravioli of ${b.toLowerCase()}.`,
      (a, b, c) => `Pressed ${a.toLowerCase()} terrine, ${b.toLowerCase()} aspic, ${c.toLowerCase()} crumb.`,
      (a, b, c) => `${a} sashimi on a slate, ${b.toLowerCase()} ponzu, one grain of ${c.toLowerCase()}.`,
      (a, b) => `A light ${a.toLowerCase()} tart, ${b.toLowerCase()} purée, built on pastry I laminated this morning.`,
      (a, b) => `${a} with ${b.toLowerCase()}. That is the dish. There is no trick.`,
    ],
    c2: [
      (a, b, c) => `Slow-braised ${a.toLowerCase()} with caramelised ${b.toLowerCase()}, finished beside roasted ${c.toLowerCase()}.`,
      (a, b) => `${a} two ways, seared then glazed, on a bed of buttered ${b.toLowerCase()}.`,
      (a, b, c) => `Pan-roasted ${a.toLowerCase()}, ${b.toLowerCase()} purée, ${c.toLowerCase()} jus. Three components, nothing more.`,
      (a, b, c) => `${a} roasted on the bone, ${b.toLowerCase()} demi-glace, a ribbon of ${c.toLowerCase()}.`,
      (a, b, c) => `Coq-style ${a.toLowerCase()} in ${b.toLowerCase()} reduction, bitter ${c.toLowerCase()} alongside.`,
      (a, b, c) => `${a} under a ${b.toLowerCase()} crust, ${c.toLowerCase()} confit at its side.`,
      (a, b) => `Sous-vide ${a.toLowerCase()} for forty-eight hours, seared in ${b.toLowerCase()} butter at the pass.`,
      (a, b) => `${a} braised in its own fat, ${b.toLowerCase()} pickle to cut it.`,
      (a, b, c) => `A tranche of ${a.toLowerCase()}, ${b.toLowerCase()} foam, ${c.toLowerCase()} oil pulled across the plate.`,
      (a, b, c) => `${a} cassoulet, slow-cooked with ${b.toLowerCase()} and ${c.toLowerCase()}. Three days of work.`,
      (a, b, c) => `${a} en croûte, ${b.toLowerCase()} and ${c.toLowerCase()} duxelles hidden inside.`,
      (a, b, c) => `${a} roulade stuffed with ${b.toLowerCase()}, ${c.toLowerCase()} glaze, served sliced.`,
      (a, b) => `${a} rillettes on toast, ${b.toLowerCase()} jam. It is the honest version of luxury.`,
      (a, b, c) => `${a} in a ${b.toLowerCase()} bouillon, finished with ${c.toLowerCase()} beurre blanc.`,
      (a, b, c) => `${a} glazed in reduced ${b.toLowerCase()}, charred ${c.toLowerCase()} for counterpoint.`,
      (a, b, c) => `${a} fillet rested on ${b.toLowerCase()} risotto, ${c.toLowerCase()} gremolata, nothing else.`,
      (a, b, c) => `${a} tagine with ${b.toLowerCase()} and preserved ${c.toLowerCase()}. A slow, patient dish.`,
      (a, b) => `${a} hung for two weeks. Served with ${b.toLowerCase()} and respect.`,
      (a, b, c) => `Rolled ${a.toLowerCase()} with ${b.toLowerCase()} farce, ${c.toLowerCase()} sauce. Classical.`,
      (a, b, c) => `${a} poached in ${b.toLowerCase()}, resting on a cake of ${c.toLowerCase()}.`,
    ],
    c3: [
      (a, b) => `A soft ${a.toLowerCase()} mousse, ${b.toLowerCase()} coulis, a pinch of sea salt.`,
      (a, b, c) => `${a} poached in ${b.toLowerCase()}, served warm with a crisp ${c.toLowerCase()} tuile.`,
      (a) => `A single perfect ${a.toLowerCase()}, treated with respect.`,
      (a, b, c) => `${a} parfait layered with ${b.toLowerCase()} curd and ${c.toLowerCase()} crumble.`,
      (a, b, c) => `Caramelised ${a.toLowerCase()}, ${b.toLowerCase()} sorbet, ${c.toLowerCase()} tuile. Balance.`,
      (a) => `${a} soufflé. It will fall within seconds. Eat it.`,
      (a, b) => `A quiet ${a.toLowerCase()} panna cotta beside a spoon of ${b.toLowerCase()}.`,
      (a, b, c) => `${a} tart, ${b.toLowerCase()} cream, ${c.toLowerCase()} shard, baked this morning.`,
      (a, b) => `${a} and ${b.toLowerCase()}, reduced to their essence. Served in a coupe.`,
      (a, b) => `Frozen ${a.toLowerCase()} parfait with warm ${b.toLowerCase()} sauce. The contrast matters.`,
      (a, b, c) => `${a} crémeux, ${b.toLowerCase()} jelly, ${c.toLowerCase()} dust on the rim.`,
      (a) => `A ${a.toLowerCase()} petit four. One bite. Then another.`,
      (a, b) => `Poached ${a.toLowerCase()} in ${b.toLowerCase()} syrup, nothing more.`,
      (a, b, c) => `${a} mille-feuille with ${b.toLowerCase()} pastry cream, dusted with ${c.toLowerCase()}.`,
      (a) => `${a} clafoutis in a little copper dish. My grandmother's exact recipe.`,
      (a, b, c) => `${a} ice cream, ${b.toLowerCase()} reduction, a single crystal of ${c.toLowerCase()}.`,
      (a, b) => `${a} mousse under a ${b.toLowerCase()} glaze, mirror-smooth.`,
      (a, b) => `${a} financier beside a thimble of ${b.toLowerCase()} liqueur.`,
      (a, b) => `${a} and ${b.toLowerCase()}. A pairing I have been thinking about for twenty years.`,
      (a, b) => `Warm ${a.toLowerCase()} galette, a scoop of ${b.toLowerCase()} crème fraîche.`,
    ],
  },
  goblin: {
    c1: [
      (a, b, c) => `You take a raw ${a.toLowerCase()}, cram it into a ${b.toLowerCase()}, wash it down with ${c.toLowerCase()} juice. DELISH.`,
      (a, b) => `${a} but with the skin still on. Lick the ${b.toLowerCase()} after. Don't judge.`,
      (a, b, c) => `I found ${a.toLowerCase()} behind the dumpster. Wrapped it in ${b.toLowerCase()}. Don't ask about the ${c.toLowerCase()}.`,
      (a, b) => `Whole ${a.toLowerCase()}. Bite it. Chase with ${b.toLowerCase()}. CHEW WITH MOUTH OPEN.`,
      (a, b) => `${a} mashed with ${b.toLowerCase()} in my bare hands. That's the starter. YOU'RE WELCOME.`,
      (a, b) => `A ${a.toLowerCase()} stuck on a fork. A ${b.toLowerCase()} stuck on the other fork. Two forks.`,
      (a, b) => `${a} dipped in melted ${b.toLowerCase()}. Licked off a rock. Rock goes back.`,
      (a, b) => `${a} eaten straight from the bag. ${b} was the bag.`,
      (a) => `Whole raw ${a.toLowerCase()}. I'm not washing it. That's flavour.`,
      (a, b, c) => `${a}, ${b.toLowerCase()}, and ${c.toLowerCase()} all in a sock. SHAKE. POUR INTO MOUTH.`,
      (a, b) => `${a} in one hand, ${b.toLowerCase()} in the other. You eat them simultaneously. COMMIT.`,
      (a, b) => `A ${a.toLowerCase()} I dropped. Picked it up. Rolled it in ${b.toLowerCase()}. Five second rule extended.`,
      (a, b) => `${a} sucked through a ${b.toLowerCase()} like a straw. I invented this. It's mine now.`,
      (a, b) => `Raw ${a.toLowerCase()} with teeth marks in it from the goblin before me. ${b} for crunch.`,
      (a, b) => `${a} and ${b.toLowerCase()} chewed simultaneously. DO NOT SEPARATE THE FLAVOURS.`,
      (a, b) => `${a} pounded with a shoe. Served on the shoe. ${b} optional.`,
      (a, b) => `Just a fistful of ${a.toLowerCase()}. ${b} if you're being fancy.`,
      (a, b) => `${a} rolled in dirt. That's ${b.toLowerCase()}. I'm calling it ${b.toLowerCase()}.`,
      (a, b) => `${a} sliced with my teeth. Dipped in cold ${b.toLowerCase()}. GRRRRRR.`,
      (a, b) => `You open a ${a.toLowerCase()}. Something wet falls out. That's the ${b.toLowerCase()}. Drink it first.`,
    ],
    c2: [
      (a, b, c) => `Whole ${a.toLowerCase()}, stuffed with ${b.toLowerCase()} and dipped in warm ${c.toLowerCase()}. EAT WITH HANDS.`,
      (a, b) => `${a} smashed flat, pan-fried in ${b.toLowerCase()} grease until the smoke alarm screams.`,
      (a, b, c) => `Boiled ${a.toLowerCase()} and ${b.toLowerCase()} in the same pot with a ${c.toLowerCase()}. Drain nothing.`,
      (a, b, c) => `${a} wrapped in ${b.toLowerCase()} and ${c.toLowerCase()}. Stapled shut. Air-fried.`,
      (a, b) => `A bucket of ${a.toLowerCase()} with ${b.toLowerCase()} poured over it. You eat out of the bucket.`,
      (a, b, c) => `${a} skewered on a stick, blasted with ${b.toLowerCase()}, ${c.toLowerCase()} on the side for licking.`,
      (a, b) => `${a} fried in lard with ${b.toLowerCase()} until unrecognisable. UNRECOGNISABLE IS THE GOAL.`,
      (a, b) => `Whole ${a.toLowerCase()} in a ${b.toLowerCase()} stew. NO UTENSILS. FINGERS ONLY.`,
      (a, b, c) => `${a} pie crust. ${b} filling. ${c} crammed on top. BAKED IN ANGER.`,
      (a, b) => `${a} bolognese except the sauce is just ${b.toLowerCase()} and I scream into it.`,
      (a, b, c) => `${a} curry with ${b.toLowerCase()} and ${c.toLowerCase()}. Eat from the pot. The pot is now yours.`,
      (a, b, c) => `${a} and ${b.toLowerCase()} in a toasted ${c.toLowerCase()} bun. With hot sauce. MORE HOT SAUCE.`,
      (a, b, c) => `${a} deep-fried. ${b} deep-fried. ${c} deep-fried. The oil is now dinner.`,
      (a, b, c) => `${a} stuffed inside a ${b.toLowerCase()} stuffed inside a ${c.toLowerCase()}. TURDUCKEN ENERGY.`,
      (a, b) => `A mountain of ${a.toLowerCase()} with ${b.toLowerCase()} gravy. Climb it. EAT THE MOUNTAIN.`,
      (a, b) => `${a} chopped with a butter knife because the sharp one is in the sink. Tossed with ${b.toLowerCase()}.`,
      (a, b, c) => `${a} grilled on a shovel over an open flame. ${b} on top. ${c} thrown in during.`,
      (a, b) => `${a} stir-fried with ${b.toLowerCase()}. Half the pan is on fire. THAT'S THE SMOKE FLAVOUR.`,
      (a, b, c) => `${a} in a burrito with ${b.toLowerCase()} and ${c.toLowerCase()}. The burrito is a tea towel.`,
      (a, b) => `Whole ${a.toLowerCase()}, microwaved while still frozen, ${b.toLowerCase()} poured over. CRUNCHY.`,
    ],
    c3: [
      (a, b) => `${a} dusted in crushed ${b.toLowerCase()}. Microwave 90 seconds. Eat standing up.`,
      (a, b, c) => `Raw ${a.toLowerCase()} rolled in ${b.toLowerCase()} and ${c.toLowerCase()}. My mum made this.`,
      (a) => `Just a ${a.toLowerCase()}. The whole thing. With the skin.`,
      (a, b) => `${a} stuffed inside a ${b.toLowerCase()}. That's the dessert. I LICKED THE PLATE.`,
      (a, b) => `${a} dipped in molten ${b.toLowerCase()}. I burned my tongue. WORTH IT.`,
      (a, b) => `Frozen ${a.toLowerCase()} on a stick with ${b.toLowerCase()} sprinkles. EAT BEFORE IT DRIPS.`,
      (a, b) => `${a} jam straight out of the jar. With a ${b.toLowerCase()} as the spoon.`,
      (a, b, c) => `${a} and ${b.toLowerCase()} in a mug. Cover with ${c.toLowerCase()}. Put in microwave. HOPE.`,
      (a, b) => `Whole ${a.toLowerCase()}, dipped in ${b.toLowerCase()} sauce. No plate. Crouched on the floor.`,
      (a, b) => `${a} cake from a box. Frosting from another box. ${b} because I had one.`,
      (a, b) => `${a} smashed into ${b.toLowerCase()} ice cream. EATEN WITH A FORK.`,
      (a, b) => `${a} sandwich: just ${a.toLowerCase()} between two ${b.toLowerCase()}. PURE GOBLIN.`,
      (a, b, c) => `Sticky ${a.toLowerCase()} with ${b.toLowerCase()} drizzle and ${c.toLowerCase()} crumbs from the bottom of the bag.`,
      (a, b) => `${a} dunked in warm ${b.toLowerCase()}. Don't tell anyone. Tell everyone.`,
      (a) => `A ${a.toLowerCase()} eaten directly over the sink so it doesn't count.`,
      (a, b) => `${a} crumble but the crumble is just ${b.toLowerCase()} smashed with a hammer.`,
      (a, b, c) => `${a}, ${b.toLowerCase()} and ${c.toLowerCase()} blended into a smoothie. Drunk warm. CHUNKY.`,
      (a, b) => `${a} bread pudding but I skipped the bread. Just ${a.toLowerCase()} and ${b.toLowerCase()}.`,
      (a) => `${a} fudge. Made in a saucepan. Scraped out with fingers. THE PAN IS DINNER.`,
      (a, b) => `Raw ${a.toLowerCase()} with cream squirted out of a can directly into my mouth after. ${b} chaser.`,
    ],
  },
  haiku: {
    c1: [
      (a, b) => `Cold ${a.toLowerCase()} awakens,\nquiet ${b.toLowerCase()} listens nearby.\nThe meal begins now.`,
      (a) => `${a} on the plate.\nA single breath of flavour.\nNothing more is said.`,
      (a, b, c) => `Thin ${a.toLowerCase()}, then ${b.toLowerCase()}.\n${c} whispers underneath.\nBalance. That is all.`,
      (a, b) => `First bite of ${a.toLowerCase()},\n${b} blooms on the tongue, quiet.\nThe day has arrived.`,
      (a, b) => `Morning ${a.toLowerCase()} shines.\n${b} rests at its quiet side.\nHunger steps forward.`,
      (a, b) => `${a} and ${b.toLowerCase()}.\nTwo old friends meet on a plate.\nNeither speaks. Both smile.`,
      (a, b) => `One drop of ${a.toLowerCase()}.\n${b} trembles, then holds its ground.\nA small brave moment.`,
      (a, b, c) => `${a}'s pale surface.\n${b} and ${c.toLowerCase()} lie across.\nSunlight through the rice.`,
      (a, b) => `Sharp ${a.toLowerCase()} arrives.\n${b} softens the edge of it.\nThe cold plate warms up.`,
      (a, b) => `${a}, still and small.\nA single grain of ${b.toLowerCase()}.\nThis is everything.`,
      (a, b) => `${a} opens slow.\n${b} waits beside the parsley.\nFirst spoon finds the sea.`,
      (a, b, c) => `${a} and ${b.toLowerCase()},\nThin as memory of rain.\n${c} holds the dew.`,
      (a, b) => `Quiet ${a.toLowerCase()} bed.\n${b} dreams above like a leaf.\nNo voice is needed.`,
      (a, b) => `${a} on the tongue.\n${b}'s shadow-sm falls behind it.\nWe begin to eat.`,
      (a, b) => `Just ${a.toLowerCase()}. Just that.\n${b} sighs against the stone plate.\nThe chef has bowed out.`,
      (a, b) => `Chilled ${a.toLowerCase()} awaits.\n${b} rests in its shadow, cool.\nA short, clean prayer.`,
      (a, b) => `${a} slices, three.\n${b} drips from the knife, golden.\nMouth opens, and waits.`,
      (a, b) => `A ${a.toLowerCase()} in light.\n${b} flickers at its feet.\nSomething small begins.`,
      (a, b) => `${a} on the stone.\n${b} dust falls over it like\nsnow on a lantern.`,
      (a, b, c) => `${a} meets ${b.toLowerCase()}.\n${c} bows its head and withdraws.\nWe taste and are still.`,
    ],
    c2: [
      (a, b) => `Heat meets ${a.toLowerCase()}'s flesh.\n${b} sighs, turns golden brown.\nSteam climbs up slowly.`,
      (a, b, c) => `${a}'s weight on tongue.\n${b} and ${c.toLowerCase()} hold the field.\nNoon has passed us by.`,
      (a, b) => `${a} roasts in peace.\n${b} cradles it like old earth.\nThe kitchen breathes warm.`,
      (a, b) => `Slow ${a.toLowerCase()} simmers.\n${b}'s bones give up their secret.\nA long afternoon.`,
      (a, b, c) => `Fire finds ${a.toLowerCase()}.\n${b} listens to the sizzle.\n${c} waits its turn.`,
      (a, b) => `${a} in the pan.\n${b}'s fat paints the cast iron gold.\nHunger grows sharper.`,
      (a, b, c) => `${a} with ${b.toLowerCase()}\ncurls together in the pot.\n${c} rests, patient.`,
      (a, b) => `Braised ${a.toLowerCase()} lets go.\n${b} reduces into itself.\nThe sauce remembers.`,
      (a, b) => `${a} on the fork.\n${b} glistens beside it still.\nA long silence falls.`,
      (a, b, c) => `Charred ${a.toLowerCase()}'s edge.\n${b} hums beneath like a drum.\n${c} keeps the beat.`,
      (a, b) => `${a} comes apart.\n${b} has been waiting for hours.\nThe lid comes off clean.`,
      (a, b) => `${a} glazed in ${b.toLowerCase()},\nlight catches the lacquered edge.\nWe eat with our eyes.`,
      (a, b, c) => `${a} in ${b.toLowerCase()} broth.\n${c} floats like a small boat.\nSteam rises and leaves.`,
      (a, b) => `The ${a.toLowerCase()} is done.\n${b} has done all that it can.\nThe plate is waiting.`,
      (a, b) => `${a} weighs the knife.\n${b} pools beneath the first cut.\nA warm coin of sauce.`,
      (a, b, c) => `${a}'s quiet crust.\n${b} underneath it, yielding.\n${c} sings on the side.`,
      (a, b) => `${a} and ${b.toLowerCase()}\nbraised into a single note.\nDinner has arrived.`,
      (a, b, c) => `Stewed ${a.toLowerCase()} softens.\n${b} kneels beside it in the pot.\n${c} learns patience.`,
      (a, b) => `${a} on black stone.\n${b}'s oil shines in the dim light.\nThe main course is here.`,
      (a, b, c) => `${a} in a bowl.\n${b} steams. ${c} rests at the rim.\nA quiet abundance.`,
    ],
    c3: [
      (a) => `${a} ends the day.\nSweet, then quiet, then goodnight.\nThe bowl sits empty.`,
      (a, b) => `A slow ${a.toLowerCase()} dream,\n${b} sings above the plate.\nRest now, small stomach.`,
      (a, b) => `Warm ${a.toLowerCase()} farewell.\n${b} whispers through the candle.\nEyes close, sated, soft.`,
      (a, b) => `${a} melts slowly.\n${b} catches the last sunlight.\nThe meal is released.`,
      (a, b, c) => `${a} on the spoon.\n${b}'s memory lingers long.\n${c} says good night.`,
      (a, b) => `${a} coolness, gone.\n${b} stays on the tongue, patient.\nThe moon is rising.`,
      (a, b) => `Sweet ${a.toLowerCase()} descends.\n${b} curls beneath like a cat.\nPlate: a small white moon.`,
      (a, b) => `${a} with ${b.toLowerCase()}.\nTwo voices, one lullaby.\nThe night has begun.`,
      (a, b) => `${a} dissolves, slow.\n${b} keeps it company still.\nNo sound from the glass.`,
      (a, b) => `Final ${a.toLowerCase()} bite.\n${b} echoes across the tongue.\nSilence. Then contentment.`,
      (a, b, c) => `${a}, sweet and cool.\n${b} shimmers like a small pond.\n${c} dreams nearby.`,
      (a, b) => `${a} tucked in rice.\n${b} sings quiet lullabies.\nThe stars are coming.`,
      (a, b) => `${a} on porcelain.\n${b} drawn thin as a haiku.\nThe day lays itself down.`,
      (a, b) => `${a} and ${b.toLowerCase()}\nlean on each other gently.\nThe table forgives us.`,
      (a, b) => `${a} coulis drawn.\n${b} sits still upon the plate.\nOne last breath. Goodbye.`,
      (a, b) => `${a} warmth fades now.\n${b} holds the last of the gold.\nThe candle goes out.`,
      (a, b) => `${a} in a cup.\n${b} drifts upward as steam does.\nThe room is empty.`,
      (a, b) => `Just a ${a.toLowerCase()}.\n${b} sighs, then stops sighing.\nThe meal closes gently.`,
      (a, b) => `${a} cools on glass.\n${b} is already asleep.\nThank you for the bread.`,
      (a, b, c) => `${a} says farewell.\n${b} and ${c.toLowerCase()} bow, and are gone.\nThe chef bows as well.`,
    ],
  },
  critic: {
    c1: [
      (a, b) => `The ${a.toLowerCase()} arrives lukewarm. The ${b.toLowerCase()} is an afterthought. It's fine.`,
      (a, b, c) => `A ${a.toLowerCase()}, ${b.toLowerCase()} and ${c.toLowerCase()} terrine that someone clearly Googled at 4pm.`,
      (a) => `${a}. Just ${a.toLowerCase()}. Plating reads as lazy rather than restrained.`,
      (a, b) => `A ${a.toLowerCase()} "amuse" with ${b.toLowerCase()}, and the scare quotes are, again, doing heavy lifting.`,
      (a, b) => `The ${a.toLowerCase()} tartare is dressed in what tastes like apology and ${b.toLowerCase()}.`,
      (a, b) => `${a} on a spoon. ${b} on the side. We're meant to be impressed. We are not.`,
      (a) => `A ${a.toLowerCase()} crudo. The knife work suggests someone who learned on YouTube.`,
      (a, b) => `${a} with ${b.toLowerCase()} foam. The foam has collapsed. So, frankly, has the concept.`,
      (a) => `The ${a.toLowerCase()} carpaccio is thick enough to read through. Generous of them.`,
      (a, b) => `${a} ceviche, ${b.toLowerCase()} "coulis." Bold quotation marks from a kitchen that doesn't own them.`,
      (a, b) => `A ${a.toLowerCase()} "reimagined" with ${b.toLowerCase()}. One wishes they had not.`,
      (a, b) => `${a} pâté on a wafer of ${b.toLowerCase()}. The pâté wants to leave. Let it.`,
      (a, b) => `${a} with a whisper of ${b.toLowerCase()}. The whisper is, regrettably, audible.`,
      (a, b) => `The kitchen has paired ${a.toLowerCase()} with ${b.toLowerCase()}. Bold. Wrong, but bold.`,
      (a, b, c) => `A ${a.toLowerCase()} cup, ${b.toLowerCase()} "jelly," ${c.toLowerCase()} dust. The dust is doing a lot of conceptual work.`,
      (a) => `${a} arrives cold. It was, presumably, meant to. Presumably.`,
      (a) => `The ${a.toLowerCase()} is "deconstructed." As was, apparently, the intention.`,
      (a, b, c) => `${a}, ${b.toLowerCase()}, ${c.toLowerCase()}, and a quote from Alice Waters on the menu. One of these is overreaching.`,
      (a, b) => `A ${a.toLowerCase()} with ${b.toLowerCase()} on a slate. The slate is 2014 calling. They want it back.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} and ${c.toLowerCase()}. Three ingredients. Three opportunities. Three misses.`,
    ],
    c2: [
      (a, b) => `The ${a.toLowerCase()} is overcooked. The ${b.toLowerCase()} is underseasoned. Neither seems aware of the other.`,
      (a, b, c) => `A ${a.toLowerCase()} with ${b.toLowerCase()} reduction and a ${c.toLowerCase()} "element". The quote marks are doing work here.`,
      (a, b) => `${a} braised into submission. ${b} as a sort of apology alongside.`,
      (a) => `${a} "two ways." Both ways, regrettably, are the wrong way.`,
      (a) => `The ${a.toLowerCase()} is served pink. The kitchen insists this is intentional. The diner remains unconvinced.`,
      (a, b) => `${a} with ${b.toLowerCase()} glaze: a glaze in name only. Essentially a liquid.`,
      (a, b) => `A ${a.toLowerCase()} "Wellington" wrapped in ${b.toLowerCase()}. Wellington would, one assumes, sue.`,
      (a) => `${a} served on a plank. The plank is the most distinguished element.`,
      (a, b, c) => `${a} and ${b.toLowerCase()} on a bed of ${c.toLowerCase()}. The bed has not been made in some time.`,
      (a, b) => `The ${a.toLowerCase()} carries notes of ${b.toLowerCase()} and, disconcertingly, dish soap.`,
      (a) => `${a} sous-vide to within an inch of its life. Past that inch, actually.`,
      (a, b) => `${a} with ${b.toLowerCase()}, "finished" tableside. Finished being the operative, and final, word.`,
      (a, b) => `A ${a.toLowerCase()} curry. The ${b.toLowerCase()} is present. The curry is not.`,
      (a, b) => `${a} in a ${b.toLowerCase()} broth that reads more like a suggestion of broth.`,
      (a, b) => `The ${a.toLowerCase()} is dry. The ${b.toLowerCase()} sauce is watery. Somewhere, in theory, there is a mean.`,
      (a, b) => `${a} with ${b.toLowerCase()} "jus." That's tap water and ambition, and I can tell.`,
      (a) => `A ${a.toLowerCase()} roulade that unrolls the moment you look at it. Confidence issues.`,
      (a, b) => `${a} glazed in ${b.toLowerCase()}. "Glazed" is generous. "Coated" is accurate. "Splashed" is honest.`,
      (a, b, c) => `${a}, ${b.toLowerCase()}, ${c.toLowerCase()}, a drizzle, and a prayer. We got three out of five.`,
      (a) => `The ${a.toLowerCase()} has been "elevated." To where, precisely, remains unclear.`,
    ],
    c3: [
      (a) => `A ${a.toLowerCase()} dessert. Competent. Forgettable. We've eaten worse.`,
      (a, b) => `${a} and ${b.toLowerCase()}: a pairing that peaked on Pinterest in 2014.`,
      (a) => `${a} crème brûlée. The brûlée has not been brûlée'd.`,
      (a, b) => `${a} "mille-feuille" with ${b.toLowerCase()}. More of a deux-feuille, if we're being strict.`,
      (a) => `A ${a.toLowerCase()} sorbet. Tastes primarily of freezer.`,
      (a) => `${a} panna cotta that stands up so firmly it should testify in court.`,
      (a, b, c) => `${a} tart. ${b} cream. ${c} "crumble," which is simply small pieces of ${c.toLowerCase()}.`,
      (a) => `The ${a.toLowerCase()} soufflé arrived deflated, as one suspects did the pastry chef.`,
      (a, b) => `${a} ice cream with ${b.toLowerCase()} "reduction." It's just ${b.toLowerCase()}.`,
      (a) => `A ${a.toLowerCase()} mousse that prefers not to be mousse. One respects its autonomy.`,
      (a) => `${a} cheesecake "with a twist." The twist is that it's mediocre.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} and ${c.toLowerCase()}. A dessert in the way a postcard is a novel.`,
      (a) => `${a} financier, I'm told. Financière, frankly, in the sense that I've been robbed.`,
      (a) => `A ${a.toLowerCase()} galette that would benefit from not being this particular galette.`,
      (a, b) => `${a} ganache on a ${b.toLowerCase()} wafer. The wafer tastes of refrigerator.`,
      (a) => `${a} "three ways." None of the three are, technically, dessert.`,
      (a) => `A slice of ${a.toLowerCase()} cake from, one suspects, earlier this decade.`,
      (a, b, c) => `${a} parfait. ${b} layer. ${c} layer. A layer cake of increasing disappointment.`,
      (a, b) => `${a} tuile. ${b} coulis. A small, sad architecture.`,
      (a, b) => `${a} and ${b.toLowerCase()}. We have, at last, arrived at the end of this meal. Blessedly.`,
    ],
  },
  tourist: {
    c1: [
      (a, b, c) => `Oh my gosh, they put ${a.toLowerCase()}, ${b.toLowerCase()} AND ${c.toLowerCase()} on the same plate?? I'm taking a photo!!`,
      (a) => `I've never had ${a.toLowerCase()} before. The menu says it's a local specialty!!`,
      (a, b) => `The waiter said this ${a.toLowerCase()} and ${b.toLowerCase()} thing is what everyone orders. I'm IN.`,
      (a, b) => `They brought out little bowls of ${a.toLowerCase()} and ${b.toLowerCase()} and I almost cried, guys.`,
      (a) => `Guidebook says "don't miss the ${a.toLowerCase()}." Reader, I did NOT miss the ${a.toLowerCase()}.`,
      (a, b) => `I pointed at a menu picture and they brought me ${a.toLowerCase()} with ${b.toLowerCase()}. BEST GAMBLE EVER.`,
      (a, b) => `The ${a.toLowerCase()} appetizer has little ${b.toLowerCase()} shapes on top?? I'm asking for the recipe!!`,
      (a, b) => `They served the ${a.toLowerCase()} in a tiny copper dish with ${b.toLowerCase()}. I'm taking the dish. (Kidding. Maybe.)`,
      (a) => `An old lady at the next table said the ${a.toLowerCase()} is from her village and I TEARED UP.`,
      (a, b) => `${a} and ${b.toLowerCase()} together?? At HOME we'd never!!! This is EDUCATIONAL.`,
      (a, b) => `${a} on a crostini with ${b.toLowerCase()}. I've eaten six already. Don't tell my husband.`,
      (a) => `I ordered the ${a.toLowerCase()} on instinct and I AM BEING REWARDED.`,
      (a, b) => `They asked if I wanted ${a.toLowerCase()} or ${b.toLowerCase()}. I said YES. And they LAUGHED. And brought BOTH.`,
      (a, b) => `The ${a.toLowerCase()} plate has a little flag on it?? Is the flag edible?? I'm eating the ${b.toLowerCase()} too.`,
      (a, b, c) => `${a}, ${b.toLowerCase()}, ${c.toLowerCase()}: three little spoons and a story from the chef. I'M WEEPING.`,
      (a, b) => `The menu is in another language but the ${a.toLowerCase()} and ${b.toLowerCase()} both came with a SMILE.`,
      (a) => `This ${a.toLowerCase()} starter tastes like the ocean. I HAVE NEVER BEEN NEAR THIS OCEAN.`,
      (a, b) => `A tiny ${a.toLowerCase()} tart with ${b.toLowerCase()}. It's the size of a coin and I AM CHANGED.`,
      (a, b) => `The ${a.toLowerCase()} came on a seashell with ${b.toLowerCase()}. I'm screaming.`,
      (a, b) => `They brought the ${a.toLowerCase()} out on FIRE. With ${b.toLowerCase()}. FIRE. OH MY GOD.`,
    ],
    c2: [
      (a, b) => `So apparently you eat the ${a.toLowerCase()} with your hands?? And dip it in the ${b.toLowerCase()}?? AMAZING.`,
      (a, b, c) => `${a}, ${b.toLowerCase()} and ${c.toLowerCase()} all together. My guidebook said this is "a must try" and I GET IT NOW.`,
      (a, b) => `The waiter just set a WHOLE ${a.toLowerCase()} in front of me with ${b.toLowerCase()} and WINKED.`,
      (a, b) => `There's a LIVE ${a.toLowerCase()} on my plate?? With ${b.toLowerCase()}?? I AM LEARNING SO MUCH.`,
      (a, b) => `The ${a.toLowerCase()} came in a tagine and they wouldn't open it until I said a PHRASE. The ${b.toLowerCase()} was INSIDE.`,
      (a, b, c) => `${a} stew with ${b.toLowerCase()} and ${c.toLowerCase()}. Grandmother recipe. I told THE TABLE.`,
      (a, b) => `I ordered ${a.toLowerCase()} and they asked HOT or VERY HOT. Dear reader, I chose VERY HOT. I am ${b.toLowerCase()} now.`,
      (a, b) => `${a} wrapped in a HUGE ${b.toLowerCase()} leaf. I'm unwrapping it like a birthday present. SOBBING.`,
      (a, b) => `They brought out the ${a.toLowerCase()} on a flaming sword?? With ${b.toLowerCase()}?? A FLAMING SWORD.`,
      (a, b) => `The ${a.toLowerCase()} is served inside a hollowed-out ${b.toLowerCase()} and I've never been more alive.`,
      (a, b) => `My husband ordered ${a.toLowerCase()}. I ordered ${b.toLowerCase()}. WE BOTH WIN.`,
      (a, b) => `The chef came out and hand-cut the ${a.toLowerCase()} tableside with ${b.toLowerCase()} sauce. I CLAPPED.`,
      (a, b) => `${a} baked in a salt crust with ${b.toLowerCase()}. They cracked it with a HAMMER. WITH A HAMMER.`,
      (a, b) => `A WHOLE ${a.toLowerCase()} rolled out on a cart. With ${b.toLowerCase()}. WHEELS AND EVERYTHING.`,
      (a, b) => `The ${a.toLowerCase()} came on a BED of ${b.toLowerCase()}. Like an actual bed. The ${a.toLowerCase()} is TUCKED IN.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} sauce and ${c.toLowerCase()} dumplings. I've taken eleven photos and zero bites.`,
      (a, b) => `They gave me a BIB. FOR A ${a.toLowerCase()}. With ${b.toLowerCase()}. I am wearing the bib. I am committed.`,
      (a, b) => `The ${a.toLowerCase()} is INSIDE a ${b.toLowerCase()}. A ${a.toLowerCase()} WITHIN a ${b.toLowerCase()}. I'M THROUGH THE LOOKING GLASS.`,
      (a, b) => `They pour the ${a.toLowerCase()} broth over the ${b.toLowerCase()} AT THE TABLE. I'm going to do this at every dinner back home.`,
      (a, b, c) => `${a} stuffed with ${b.toLowerCase()} and ${c.toLowerCase()}. The waiter said "enjoy" and I said "I ALREADY AM."`,
    ],
    c3: [
      (a, b) => `${a} ice cream with a ${b.toLowerCase()} drizzle. I'm bringing this home if I can fit it in my suitcase.`,
      (a) => `The ${a.toLowerCase()} tart just arrived and I legitimately gasped.`,
      (a, b) => `${a} gelato in SIX FLAVOURS. I ordered all six. With ${b.toLowerCase()}. DON'T JUDGE ME.`,
      (a, b) => `The ${a.toLowerCase()} cake is SHAPED LIKE a ${b.toLowerCase()}!! I don't want to eat it!! BUT I WILL.`,
      (a, b) => `They lit the ${a.toLowerCase()} on fire with ${b.toLowerCase()} liqueur and I COULD NOT KEEP IT TOGETHER.`,
      (a) => `The ${a.toLowerCase()} dessert came with a TINY SPOON and a TINY FORK. Everything is TINY. I love it.`,
      (a, b) => `${a} and ${b.toLowerCase()} in a FLAKY PASTRY. I moaned. Out loud. In the restaurant.`,
      (a, b) => `The chef made the ${a.toLowerCase()} "especially for me" with ${b.toLowerCase()}. For. Me. ME.`,
      (a) => `A ${a.toLowerCase()} soufflé!! They had to warn me it would take 20 MINUTES!! I WOULD WAIT FOREVER.`,
      (a, b) => `${a} SORBET between courses?? With ${b.toLowerCase()}?? I didn't know this was A THING.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} and ${c.toLowerCase()} shaved over the top with a tiny tool. MY HUSBAND IS BORED. I AM NOT.`,
      (a, b) => `They brought out a WHOLE ${a.toLowerCase()} and cut a slice tableside. WITH ${b.toLowerCase()} SAUCE. I TIPPED HUGELY.`,
      (a, b) => `${a} pudding with warm ${b.toLowerCase()} poured over it. I'm writing a POSTCARD about this. RIGHT NOW.`,
      (a, b) => `The ${a.toLowerCase()} dessert came under a GLASS DOME full of smoke. The SMOKE TASTED LIKE ${b.toLowerCase()}. WITCHCRAFT.`,
      (a, b, c) => `${a} crêpes flambéed AT MY TABLE with ${b.toLowerCase()} and ${c.toLowerCase()}!! There was APPLAUSE!!`,
      (a, b) => `A single ${a.toLowerCase()} truffle arrived on a GOLD LEAF with ${b.toLowerCase()}. Is this real life??`,
      (a, b) => `${a} fritters rolled in ${b.toLowerCase()} sugar. The lady next to me ORDERED ANOTHER and I did TOO.`,
      (a) => `The ${a.toLowerCase()} panna cotta WOBBLES and I asked them to bring it back out so I could FILM THE WOBBLE.`,
      (a, b) => `${a} in a cone, with ${b.toLowerCase()} on top, from a stall the taxi driver recommended. BEST RECOMMENDATION OF MY LIFE.`,
      (a, b, c) => `${a} and ${b.toLowerCase()}, with ${c.toLowerCase()} syrup. The MENU SAYS "traditional" and I AM A BELIEVER.`,
    ],
  },
  kid: {
    c1: [
      (a, b) => `Okay so it's a ${a.toLowerCase()} but it's SHAPED LIKE A DINOSAUR and the sauce is ${b.toLowerCase()} and you dip it.`,
      (a) => `Just ${a.toLowerCase()}. No touching the plate. No green stuff.`,
      (a, b, c) => `It's called a ${a} Triple-${b} ${c}-Splosion and if you eat it you get a sticker.`,
      (a, b) => `${a} but the ${b.toLowerCase()} CAN'T TOUCH IT. If it touches it's ruined FOREVER.`,
      (a) => `${a} cut into stars. The stars are for the HEROES. I'm a HERO. I get all of them.`,
      (a, b) => `${a} shaped like my FACE but smaller. With ${b.toLowerCase()} HAIR. The hair is the best part.`,
      (a, b) => `Dad says it's ${a.toLowerCase()}. I say it's a TREASURE and the ${b.toLowerCase()} is GOLD.`,
      (a) => `${a} on a tiny toothpick. The toothpick is a SWORD. It was a sword BEFORE it was a toothpick.`,
      (a, b) => `${a} in a little cup with ${b.toLowerCase()} sauce. THREE cups. NO GROWN-UPS.`,
      (a, b) => `A ${a.toLowerCase()} BUT with ${b.toLowerCase()} EYES made of raisins. I NAMED HIM KEVIN. I'm eating KEVIN.`,
      (a) => `It's ${a.toLowerCase()} BUT they cut off the edges. The edges are POISON. Everybody knows.`,
      (a, b) => `${a} AND ${b.toLowerCase()} but on the SAME FORK. I INVENTED this. It's called a DOUBLE FORK.`,
      (a) => `${a} nuggets in the shape of ANIMALS. I'm eating the lion last. He's the BOSS.`,
      (a, b) => `A ${a.toLowerCase()} on a plate with a SMILEY FACE made of ${b.toLowerCase()}. Even the PLATE is happy.`,
      (a) => `${a} but it's TINY TINY TINY like a MOUSE would eat it. I'm a MOUSE now.`,
      (a, b) => `You take a ${a.toLowerCase()}, you hide it in your napkin, and then you eat the ${b.toLowerCase()}. MY PLAN.`,
      (a, b) => `${a} with ${b.toLowerCase()} on a STICK. Everything is BETTER on a STICK. That's SCIENCE.`,
      (a, b) => `${a} pancake. Round. With ${b.toLowerCase()} eyes. It's looking AT ME. I don't care. EAT THE FACE.`,
      (a, b) => `A ${a.toLowerCase()} that's SECRETLY a ${b.toLowerCase()} inside. It's a TRICK. I LOVE TRICKS.`,
      (a) => `${a} but you have to eat it in ONE BITE or the spell BREAKS and the WORLD ENDS. I'm doing it.`,
    ],
    c2: [
      (a, b) => `${a} nuggets but with ${b.toLowerCase()} INSIDE. It's secret. You don't tell.`,
      (a, b, c) => `The ${a.toLowerCase()} is the dragon and the ${b.toLowerCase()} is lava and the ${c.toLowerCase()} is the sword. You eat all of them.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} BUT IN A CASTLE. The castle is made of ${c.toLowerCase()}. I'm the KING.`,
      (a, b) => `${a} pasta with ${b.toLowerCase()} sauce. The pasta is WORMS. They're GOOD worms. They're on MY team.`,
      (a, b) => `${a} pizza with ${b.toLowerCase()}. Cut into EIGHT slices. I'm eating SEVEN. Dad gets ONE.`,
      (a, b, c) => `${a} burger but TOWERING with ${b.toLowerCase()} and ${c.toLowerCase()}. So tall the top falls off. YOU EAT THE TOP FIRST.`,
      (a, b) => `${a} and ${b.toLowerCase()} in a TINY POT. It's a WITCH'S stew. But GOOD WITCH. I helped STIR.`,
      (a, b) => `${a} nuggets arranged in the shape of a T-REX. The ${b.toLowerCase()} is the blood. EVERYONE RUN.`,
      (a, b) => `${a} spaghetti that I EAT WITH MY HANDS because FORKS are for BABIES. ${b} too.`,
      (a, b, c) => `${a} tacos. You fold it. ${b} on top. ${c} is THE SECRET INGREDIENT. I'll tell you but it's SECRET.`,
      (a, b) => `${a} meatballs as big as MY FIST. I compared. They are EXACTLY FIST SIZED. Also ${b.toLowerCase()}.`,
      (a, b, c) => `${a} mac and ${b.toLowerCase()} BUT ALSO ${c.toLowerCase()} because I dared mom to put it in.`,
      (a, b) => `${a} rice with ${b.toLowerCase()} BUT you have to use CHOPSTICKS. I'm the CHOPSTICK CHAMPION.`,
      (a, b, c) => `${a} hotdog with ${b.toLowerCase()} and ${c.toLowerCase()}. LINES of mustard. STRAIGHT. I did it MYSELF.`,
      (a, b) => `${a} soup but with ${b.toLowerCase()} ALPHABETS. You can spell BUTT. I'm spelling BUTT.`,
      (a, b) => `${a} wings. ${b} sauce. I have to wash my hands AFTER but NOT DURING. That's the RULE.`,
      (a, b, c) => `${a} corndog but dipped in ${b.toLowerCase()}. And ${c.toLowerCase()}. And also ${b.toLowerCase()} again.`,
      (a, b) => `${a} stir-fry but you have to STIR it YOURSELF. With ${b.toLowerCase()}. I'M THE CHEF NOW.`,
      (a, b) => `${a} quesadilla with SECRET ${b.toLowerCase()} INSIDE. It's a treasure map. The map is IN MY TUMMY.`,
      (a, b) => `${a} with ${b.toLowerCase()} arranged so it looks like a SMILEY FACE but then I ATE THE SMILE FIRST.`,
    ],
    c3: [
      (a) => `${a} cake but HUGE. Like bigger than my head. I can do it.`,
      (a, b) => `${a} with ${b.toLowerCase()} on top and extra sprinkles and a cherry. Two cherries.`,
      (a, b) => `${a} ice cream in a cone TALLER than ME. With ${b.toLowerCase()}. I'M DOING IT.`,
      (a, b) => `${a} cupcakes. TWELVE. With ${b.toLowerCase()} frosting. I don't share. I will share ONE. MAYBE.`,
      (a, b) => `${a} brownies but EXTRA GOOEY and with ${b.toLowerCase()} CHUNKS. I licked the bowl. And the spoon. And the counter.`,
      (a, b) => `${a} pie with ${b.toLowerCase()} WHIPPED CREAM. The cream is a MOUNTAIN. I'm at the SUMMIT.`,
      (a, b, c) => `${a} with ${b.toLowerCase()} drizzle AND ${c.toLowerCase()} on top. I put the ${c.toLowerCase()} on MYSELF.`,
      (a, b) => `${a} DONUTS. FIVE OF THEM. With ${b.toLowerCase()} GLAZE. The glaze is LAVA but SWEET lava.`,
      (a) => `${a} pudding in a JAR. The jar is MINE now. Mom says no. I say YES.`,
      (a, b) => `${a} float with ${b.toLowerCase()} on top. It FIZZES and ALMOST overflows and I CAUGHT IT.`,
      (a, b) => `${a} PANCAKES but for DESSERT which is ILLEGAL. With ${b.toLowerCase()} syrup. I am a REBEL.`,
      (a, b) => `${a} cookies shaped like UNICORNS. With ${b.toLowerCase()} manes. I eat the HORN FIRST for POWER.`,
      (a, b) => `${a} cake BUT IT HAS ${b.toLowerCase()} INSIDE. WHEN YOU CUT IT, TREASURE COMES OUT. I SAW IT.`,
      (a, b) => `${a} marshmallows toasted over ${b.toLowerCase()}. BLACK ON THE OUTSIDE. GOOEY INSIDE. IT'S PERFECT. YOU'RE WRONG.`,
      (a, b) => `${a} cheesecake with ${b.toLowerCase()} swirl. I ate ONLY the swirl. Mom's face was FUNNY.`,
      (a, b, c) => `${a} banana split with ${b.toLowerCase()} AND ${c.toLowerCase()}. THREE scoops. THREE CHERRIES. I counted.`,
      (a, b) => `${a} s'mores with ${b.toLowerCase()} instead of chocolate. I INVENTED IT. I'M FAMOUS NOW.`,
      (a, b) => `${a} sundae in a GIANT BOWL shaped like a DINOSAUR. With ${b.toLowerCase()} sauce. The dinosaur is SAD. I'm EATING HIS BELLY.`,
      (a, b) => `${a} cake with ${b.toLowerCase()} CANDLES. I BLEW THEM ALL OUT. My wish was MORE ${a.toLowerCase()} CAKE.`,
      (a, b, c) => `${a} rolled in ${b.toLowerCase()} sugar and ${c.toLowerCase()} sprinkles. THREE THINGS. I ate in THREES for LUCK.`,
    ],
  },
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Deterministically partition a hand into three courses so that EVERY
 * card is used exactly once across the meal. Uses a seeded shuffle
 * (stable for a given seed) and distributes cards round-robin so the
 * split is as even as possible for any hand size:
 *
 *   hand 9 → [3, 3, 3]
 *   hand 8 → [3, 3, 2]  (dessert smallest)
 *   hand 7 → [3, 2, 2]
 *   hand 6 → [2, 2, 2]
 *   hand 5 → [2, 2, 1]
 *
 * Seed should be per (room, player) so each AI gets a stable partition
 * that's independent of other bots.
 */
export function partitionHandForCourses(
  hand: string[],
  seed: string,
): [string[], string[], string[]] {
  const rng = makeRng(seed)
  const shuffled = pickN(hand, hand.length, rng)
  const buckets: [string[], string[], string[]] = [[], [], []]
  // Round-robin into courses 0, 1, 2 so any imbalance lands on course 3.
  shuffled.forEach((id, i) => { buckets[i % 3].push(id) })
  return buckets
}

/**
 * Generate a 3-course meal for an AI player given their dealt hand.
 * Every card is used exactly once — partitioned deterministically — and
 * the text for each course only mentions the ingredients in that
 * course's partition.
 */
export function generateCoursesFor(
  styleKey: string,
  hand: string[],
  seed: string,
): ThreeCourseOutput {
  const [c1ids, c2ids, c3ids] = partitionHandForCourses(hand, seed)
  return {
    c1: generateSingleCourseFor(styleKey, c1ids, 1, `${seed}:1`),
    c2: generateSingleCourseFor(styleKey, c2ids, 2, `${seed}:2`),
    c3: generateSingleCourseFor(styleKey, c3ids, 3, `${seed}:3`),
  }
}

/**
 * Generate a SINGLE course for an AI player. `cardsForThisCourse` must
 * already be filtered to the cards this course should use — no more,
 * no less. The generated text references those card names (via the
 * persona's template library) and the returned `ids` is exactly that
 * input list, so the server-side card accounting matches the story.
 *
 * Pre-existing callers that passed "the remaining hand" still work:
 * the course's text uses up to the first 3 card names for flavor, but
 * every id from `cardsForThisCourse` is returned in `ids`.
 */
export function generateSingleCourseFor(
  styleKey: string,
  cardsForThisCourse: string[],
  course: 1 | 2 | 3,
  seed: string,
): CourseOutput {
  const templates = TEMPLATES[(styleKey as StyleKey)] ?? TEMPLATES.chef
  const rng = makeRng(seed)
  const courseKey = course === 1 ? 'c1' : course === 2 ? 'c2' : 'c3'

  // Text mentions up to 3 cards (templates take 3 slots). If the partition
  // is smaller, fill by repeating the first label — always a card name.
  // If the partition is bigger, the extra ids still count as "used" but
  // aren't named in the sentence; persona templates are narrative, not
  // exhaustive. The `ids` array tracks everything consumed.
  // Defensive: if a caller passes an empty course (shouldn't happen
  // with partitionHandForCourses), fall back to a neutral label so we
  // don't render "undefined" into the story.
  const sample = cardsForThisCourse.length > 0 ? cardsForThisCourse : [INGREDIENT_CARDS[0].id]
  const labels = sample.slice(0, 3).map(labelOf)
  while (labels.length < 3) labels.push(labels[0])
  const [a, b, c] = labels

  const t = choice(templates[courseKey], rng)
  return {
    text: t(a, b, c),
    ids: [...new Set(cardsForThisCourse)],
  }
}

/**
 * Decide an AI's vote on a specific course. Uses the persona's base
 * yes_rate, then nudges up/down based on how "weird" the story reads.
 * Deterministic per (voter, submission, course).
 */
export function decideVoteFor(
  yesRate: number,
  voterId: string,
  submissionId: string,
  course: 1 | 2 | 3,
  courseText: string,
): boolean {
  const rng = makeRng(`${voterId}:${submissionId}:${course}`)
  // Very rough "weirdness" heuristic: exclamation marks + all-caps words
  // push the bot to vote yes (chaos attracts chaos); long sober sentences
  // push it the other way. Keeps voting from looking uniformly random.
  const bangs = (courseText.match(/!/g) ?? []).length
  const capsWords = (courseText.match(/\b[A-Z]{3,}\b/g) ?? []).length
  const weird = Math.min(0.25, 0.05 * bangs + 0.08 * capsWords)
  const adjusted = Math.max(0, Math.min(1, yesRate + weird - 0.05))
  return rng() < adjusted
}
